import { supabaseClient } from '../../config/supabase';
import { entriesOf, saveContactMethods } from '../../lib/contactMethods';
import { timeZoneFieldsForSave } from '../../lib/contactTimeZone';

// BL-177 Step 2m: communication preferences and text consent belong to the client. This card never reads them into the edit form and
// never writes them (the database refuses a staff write). Staff use "Record opt-out…" / support admins "Change for client…" instead.
// BL-177 PR C: a person's phones / emails / addresses are labeled entries (contact_methods), one Primary each. The card edits them as
// lists and saves the DIFFERENCE, so an extra number is never wiped by a save. contacts.phones/emails are no longer written here: the
// database keeps that flat mirror in step. Account type is Business | Personal (account_kind); is_business follows it in the database.
export const accountKindOf = (account) => account?.account_kind || (account?.is_business ? 'business' : 'personal');

export const clientDisclosureConfig = {
    createDraft(account) {
        const contact = account.primary_contact;
        return {
            account_name: account.account_name || '',
            account_type: account.account_type || '',
            account_kind: accountKindOf(account),
            status: account.status,
            customer_tier_id: account.customer_tier_id || '',
            card_on_file: !!account.card_on_file,
            senior: !!account.senior,
            requires_human_review: !!account.requires_human_review,
            preferred_drivers: Array.isArray(account.preferred_drivers) ? account.preferred_drivers : [],
            contact_name: contact?.name || '',
            contact_first_name: contact?.first_name, contact_last_name: contact?.last_name,
            position: contact?.position || '',
            phones: entriesOf(contact, 'phone'),
            emails: entriesOf(contact, 'email'),
            addresses: entriesOf(contact, 'address'),
            // BL-177 Step 2j: IANA zone + who set it ('auto' = suggested from the address / phone, 'manual' = a person chose it)
            time_zone: contact?.time_zone || '',
            time_zone_source: contact?.time_zone ? (contact.time_zone_source || 'auto') : null,
            notes: contact?.notes || '',
            photo_url: contact?.photo_url || '',
        };
    },

    async saveDraft(account, form) {
        const contact = account.primary_contact;
        const { error: accountError } = await supabaseClient
            .from('accounts')
            .update({
                account_name: (form.account_name || '').trim(),
                account_type: form.account_type?.trim() || null,
                account_kind: form.account_kind === 'personal' ? 'personal' : 'business',
                status: form.status,
                customer_tier_id: form.customer_tier_id || null,
                card_on_file: form.card_on_file,
                senior: form.senior,
                requires_human_review: !!form.requires_human_review,
                preferred_drivers: form.preferred_drivers || [],
            })
            .eq('id', account.id);
        if (accountError) throw accountError;

        if (!contact?.id) return;
        const { error: contactError } = await supabaseClient
            .from('contacts')
            .update({
                name: (form.contact_name || '').trim(),
                ...(form.contact_first_name !== undefined ? { first_name: form.contact_first_name.trim(), last_name: (form.contact_last_name || '').trim() } : {}),
                position: form.position?.trim() || null,
                notes: form.notes?.trim() || null,
                photo_url: form.photo_url?.trim() || null,
                requires_human_review: !!form.requires_human_review,
                // a person's zone is kept; an auto zone follows the CURRENT primary address / phone (and is cleared when nothing supports it)
                ...timeZoneFieldsForSave({
                    time_zone: form.time_zone, time_zone_source: form.time_zone_source, addresses: form.addresses, phones: form.phones,
                }),
            })
            .eq('id', contact.id);
        if (contactError) throw contactError;

        for (const [kind, key] of [['phone', 'phones'], ['email', 'emails'], ['address', 'addresses']]) {
            await saveContactMethods(supabaseClient, {
                contactId: contact.id,
                kind,
                original: entriesOf(contact, kind),
                next: form[key] || [],
            });
        }
    },
};
